<template>
  <div
    v-if="modelValue"
    class="cic-overlay"
    role="dialog"
    aria-modal="true"
    :aria-label="`Crop ${title}`"
  >
    <div class="cic-dialog">
      <div class="cic-header">
        <h5 class="cic-title">
          <i class="bi bi-image me-2"></i>{{ title }}
        </h5>
        <button type="button" class="btn-close" @click="cancel" aria-label="Close"></button>
      </div>

      <div class="cic-body">
        <div
          class="cic-viewport"
          ref="viewport"
          @mousedown.prevent="startDrag"
          @mousemove="onDrag"
          @mouseup="stopDrag"
          @mouseleave="stopDrag"
          @wheel.prevent="onWheel"
          @touchstart.prevent="startTouchDrag"
          @touchmove.prevent="onTouchDrag"
          @touchend="stopDrag"
        >
          <canvas
            ref="previewCanvas"
            :width="CROP_W"
            :height="CROP_H"
            class="cic-canvas"
          ></canvas>
          <!-- Rectangular guide overlay -->
          <div class="cic-rect-guide"></div>
        </div>

        <div class="cic-zoom-row">
          <i class="bi bi-zoom-out cic-zoom-icon"></i>
          <input
            type="range"
            class="form-range cic-zoom-range"
            :min="minZoom"
            :max="maxZoom"
            step="0.005"
            :value="zoom"
            @input="onZoomSlider"
          />
          <i class="bi bi-zoom-in cic-zoom-icon"></i>
        </div>

        <p class="cic-hint">
          <i class="bi bi-arrows-move me-1"></i>Drag to reposition &nbsp;·&nbsp;
          <i class="bi bi-search me-1"></i>Scroll or slide to zoom
        </p>
      </div>

      <div class="cic-footer">
        <button type="button" class="btn btn-secondary btn-sm" @click="cancel">
          Cancel
        </button>
        <button type="button" class="btn btn-primary btn-sm" @click="confirm">
          <i class="bi bi-check-lg me-1"></i>Apply
        </button>
      </div>
    </div>

    <!-- Hidden output canvas used only during confirm export -->
    <canvas ref="outputCanvas" style="display:none"></canvas>
  </div>
</template>

<script>
export default {
  name: 'CoverImageCropper',

  props: {
    modelValue: {
      type: Boolean,
      default: false
    },
    imageSrc: {
      type: String,
      default: null
    },
    title: {
      type: String,
      default: 'Set Cover Image'
    }
  },

  emits: ['update:modelValue', 'cropped', 'cancel'],

  data() {
    return {
      // 16:9 crop dimensions
      CROP_W: 480,
      CROP_H: 270,
      zoom: 1,
      minZoom: 0.1,
      maxZoom: 4,
      offsetX: 0,
      offsetY: 0,
      dragging: false,
      lastMouseX: 0,
      lastMouseY: 0,
      image: null
    }
  },

  watch: {
    imageSrc(src) {
      if (src) this.loadImage(src)
    },
    modelValue(open) {
      if (open && this.imageSrc) this.loadImage(this.imageSrc)
    }
  },

  methods: {
    loadImage(src) {
      const img = new Image()
      img.onload = () => {
        this.image = img

        // Fit image to fill the crop area (cover behaviour)
        const fitScale = Math.max(
          this.CROP_W / img.naturalWidth,
          this.CROP_H / img.naturalHeight
        )
        this.minZoom = fitScale
        this.maxZoom = fitScale * 4
        this.zoom = fitScale
        this.offsetX = 0
        this.offsetY = 0

        this.$nextTick(() => this.draw())
      }
      img.src = src
    },

    draw() {
      const canvas = this.$refs.previewCanvas
      if (!canvas || !this.image) return

      const ctx = canvas.getContext('2d')
      const w = this.CROP_W
      const h = this.CROP_H

      ctx.clearRect(0, 0, w, h)

      const scaledW = this.image.naturalWidth * this.zoom
      const scaledH = this.image.naturalHeight * this.zoom
      const drawX = w / 2 - scaledW / 2 + this.offsetX
      const drawY = h / 2 - scaledH / 2 + this.offsetY

      ctx.drawImage(this.image, drawX, drawY, scaledW, scaledH)
    },

    clampOffset() {
      if (!this.image) return
      const w = this.CROP_W
      const h = this.CROP_H
      const scaledW = this.image.naturalWidth * this.zoom
      const scaledH = this.image.naturalHeight * this.zoom

      const maxOffX = Math.max(0, scaledW / 2 - w / 2)
      const maxOffY = Math.max(0, scaledH / 2 - h / 2)

      const freeX = scaledW < w ? w / 2 : maxOffX
      const freeY = scaledH < h ? h / 2 : maxOffY

      this.offsetX = Math.max(-freeX, Math.min(freeX, this.offsetX))
      this.offsetY = Math.max(-freeY, Math.min(freeY, this.offsetY))
    },

    // ── Mouse events ─────────────────────────────────────────────────────────
    startDrag(e) {
      this.dragging = true
      this.lastMouseX = e.clientX
      this.lastMouseY = e.clientY
    },

    onDrag(e) {
      if (!this.dragging) return
      this.offsetX += e.clientX - this.lastMouseX
      this.offsetY += e.clientY - this.lastMouseY
      this.lastMouseX = e.clientX
      this.lastMouseY = e.clientY
      this.clampOffset()
      this.draw()
    },

    stopDrag() {
      this.dragging = false
    },

    onWheel(e) {
      const delta = e.deltaY < 0 ? 0.06 : -0.06
      this.zoom = Math.max(this.minZoom, Math.min(this.maxZoom, this.zoom + delta))
      this.clampOffset()
      this.draw()
    },

    onZoomSlider(e) {
      this.zoom = parseFloat(e.target.value)
      this.clampOffset()
      this.draw()
    },

    // ── Touch events ─────────────────────────────────────────────────────────
    startTouchDrag(e) {
      if (e.touches.length === 1) {
        this.dragging = true
        this.lastMouseX = e.touches[0].clientX
        this.lastMouseY = e.touches[0].clientY
      }
    },

    onTouchDrag(e) {
      if (!this.dragging || e.touches.length !== 1) return
      this.offsetX += e.touches[0].clientX - this.lastMouseX
      this.offsetY += e.touches[0].clientY - this.lastMouseY
      this.lastMouseX = e.touches[0].clientX
      this.lastMouseY = e.touches[0].clientY
      this.clampOffset()
      this.draw()
    },

    // ── Output ────────────────────────────────────────────────────────────────
    confirm() {
      const out = this.$refs.outputCanvas
      out.width = this.CROP_W
      out.height = this.CROP_H

      const ctx = out.getContext('2d')
      ctx.clearRect(0, 0, this.CROP_W, this.CROP_H)

      const scaledW = this.image.naturalWidth * this.zoom
      const scaledH = this.image.naturalHeight * this.zoom
      const drawX = this.CROP_W / 2 - scaledW / 2 + this.offsetX
      const drawY = this.CROP_H / 2 - scaledH / 2 + this.offsetY

      ctx.drawImage(this.image, drawX, drawY, scaledW, scaledH)

      const base64 = out.toDataURL('image/jpeg', 0.92)
      this.$emit('cropped', base64)
      this.$emit('update:modelValue', false)
    },

    cancel() {
      this.$emit('cancel')
      this.$emit('update:modelValue', false)
    }
  }
}
</script>

<style scoped>
.cic-overlay {
  position: fixed;
  inset: 0;
  z-index: 1100;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
}

.cic-dialog {
  background: #fff;
  border-radius: 12px;
  width: 540px;
  max-width: 95vw;
  box-shadow: 0 8px 40px rgba(0, 0, 0, 0.3);
  overflow: hidden;
}

.cic-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #e9ecef;
}

.cic-title {
  margin: 0;
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-semibold);
  color: #343a40;
}

.cic-body {
  padding: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.cic-viewport {
  position: relative;
  width: 480px;
  height: 270px;
  max-width: calc(95vw - 40px);
  overflow: hidden;
  background: #111;
  cursor: grab;
  user-select: none;
  border-radius: 4px;
}

.cic-viewport:active {
  cursor: grabbing;
}

.cic-canvas {
  display: block;
  width: 100%;
  height: 100%;
}

.cic-rect-guide {
  position: absolute;
  inset: 0;
  pointer-events: none;
  border: 2px solid rgba(255, 255, 255, 0.6);
  border-radius: 2px;
  box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.35);
}

.cic-zoom-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 16px;
}

.cic-zoom-range {
  width: 220px;
}

.cic-zoom-icon {
  font-size: var(--font-size-md);
  color: #6c757d;
}

.cic-hint {
  font-size: var(--font-size-xs);
  color: #6c757d;
  margin-top: 8px;
  margin-bottom: 0;
  text-align: center;
}

.cic-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 14px 20px;
  border-top: 1px solid #e9ecef;
  background: #f8f9fa;
}
</style>
